import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getApiError } from '../../lib/utils';
import CustomerLayout from '../../components/layouts/CustomerLayout';
import StatusBadge from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  MapPin, 
  Send, 
  Package, 
  FileText,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  Truck,
  PenLine
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WaybillDetail = () => {
  const { waybillId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const backPath = from || '/dashboard';
  const backLabel = from === '/history' ? 'Back to History'
    : from === '/billing' ? 'Back to Billing'
    : from === '/tracking' ? 'Back to Tracking'
    : 'Back to Dashboard';
  const [waybill, setWaybill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [amendMode, setAmendMode] = useState(false);
  const [adjustMode, setAdjustMode] = useState(false);
  const [adjustedItems, setAdjustedItems] = useState([]);
  const [amendedItems, setAmendedItems] = useState([]);
  const [amendedDeliveryType, setAmendedDeliveryType] = useState('');
  const [deliveryTypes, setDeliveryTypes] = useState([]);

  const fetchWaybill = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/waybills/${waybillId}`);
      setWaybill(response.data);
    } catch (err) {
      console.error('Error fetching waybill:', err);
      toast.error('Failed to load waybill');
    } finally {
      setLoading(false);
    }
  }, [waybillId]);

  useEffect(() => {
    fetchWaybill();
  }, [fetchWaybill]);

  useEffect(() => {
    const fetchDeliveryTypes = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/delivery-types`);
        setDeliveryTypes(res.data.delivery_types || []);
      } catch (err) {
        console.error('Error fetching delivery types:', err);
      }
    };
    fetchDeliveryTypes();
  }, []);

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/waybills/${waybillId}/accept`);
      toast.success('Quote accepted!');
      // Refresh waybill data
      const response = await axios.get(`${API_URL}/api/waybills/${waybillId}`);
      setWaybill(response.data);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to accept quote'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/waybills/${waybillId}/decline`);
      toast.success('Quote declined');
      navigate(backPath);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to decline quote'));
    } finally {
      setActionLoading(false);
    }
  };

  const enterAmendMode = () => {
    setAmendedItems(
      (waybill.items || []).map(item => ({
        item_id: item.item_id,
        quantity: item.quantity,
        package_type: item.package_type,
        description: item.description
      }))
    );
    setAmendedDeliveryType(waybill.delivery_type_code || '');
    setAmendMode(true);
  };

  const cancelAmendMode = () => {
    setAmendMode(false);
    setAmendedItems([]);
    setAmendedDeliveryType('');
  };

  const updateAmendedQty = (itemId, newQty) => {
    setAmendedItems(prev =>
      prev.map(item =>
        item.item_id === itemId ? { ...item, quantity: Math.max(1, Number(newQty) || 1) } : item
      )
    );
  };

  const handleRequestAmendment = async () => {
    setActionLoading(true);
    try {
      const payload = {
        delivery_type_code: amendedDeliveryType,
        items: amendedItems.map(i => ({ item_id: i.item_id, quantity: i.quantity }))
      };
      await axios.put(`${API_URL}/api/waybills/${waybillId}/request-amendment`, payload);
      toast.success('Amendment requested — quote sent back for re-pricing');
      setAmendMode(false);
      fetchWaybill();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to request amendment'));
    } finally {
      setActionLoading(false);
    }
  };

  const enterAdjustMode = () => {
    setAdjustedItems(
      (waybill.items || []).map(item => ({
        item_id: item.item_id,
        quantity: item.quantity,
        description: item.description
      }))
    );
    setAdjustMode(true);
  };

  const cancelAdjustMode = () => {
    setAdjustMode(false);
    setAdjustedItems([]);
  };

  const updateAdjustedQty = (itemId, newQty) => {
    setAdjustedItems(prev =>
      prev.map(item =>
        item.item_id === itemId ? { ...item, quantity: Math.max(1, Number(newQty) || 1) } : item
      )
    );
  };

  const getAdjustedTotal = () => {
    if (!waybill.quote_breakdown?.line_items) return null;
    let total = 0;
    for (const adj of adjustedItems) {
      const li = waybill.quote_breakdown.line_items.find(l => l.item_id === adj.item_id);
      if (li) total += (li.unit_cost || 0) * adj.quantity;
    }
    return total;
  };

  const handleAdjustQuantity = async () => {
    setActionLoading(true);
    try {
      const payload = {
        items: adjustedItems.map(i => ({ item_id: i.item_id, quantity: i.quantity }))
      };
      const res = await axios.put(`${API_URL}/api/waybills/${waybillId}/adjust-quantity`, payload);
      toast.success(res.data.message || 'Quantities adjusted');
      setAdjustMode(false);
      fetchWaybill();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to adjust quantities'));
    } finally {
      setActionLoading(false);
    }
  };

  const downloadPDF = async (type) => {
    try {
      const endpoint = type === 'waybill' ? 'pdf' : 'quote-pdf';
      const response = await axios.get(`${API_URL}/api/waybills/${waybillId}/${endpoint}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${waybill.reference_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `R ${Number(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <CustomerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
        </div>
      </CustomerLayout>
    );
  }

  if (!waybill) {
    return (
      <CustomerLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Waybill not found</p>
          <Link to={backPath}>
            <Button className="mt-4">{backLabel}</Button>
          </Link>
        </div>
      </CustomerLayout>
    );
  }

  const showQuoteActions = waybill.status === 'quote_ready';
  const canDownloadWaybill = ['accepted', 'dispatch_scheduled', 'collected', 'in_transit', 'at_customs', 'out_for_delivery', 'delivered'].includes(waybill.status);
  const canAdjustQty = ['accepted', 'acknowledged', 'dispatch_scheduled'].includes(waybill.status) && !!waybill.quoted_amount;

  return (
    <CustomerLayout>
      <div className="animate-fade-in" data-testid="waybill-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <Link to={backPath} className="inline-flex items-center text-sm text-slate-500 hover:text-teal-700 mb-2">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {backLabel}
            </Link>
            <div className="flex items-center space-x-4">
              <h1 className="page-title">{waybill.reference_number}</h1>
              <StatusBadge status={waybill.status} />
            </div>
            <p className="text-slate-600 mt-1">
              {waybill.origin_city} → {waybill.destination_city}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {showQuoteActions && !amendMode && (
              <>
                <Button 
                  onClick={handleAccept} 
                  disabled={actionLoading}
                  className="btn-primary"
                  data-testid="accept-quote-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Accept Quote
                </Button>
                <Button 
                  onClick={enterAmendMode}
                  disabled={actionLoading}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="amend-quote-btn"
                >
                  <PenLine className="h-4 w-4 mr-2" />
                  Amend Quote
                </Button>
                <Button 
                  onClick={handleDecline} 
                  disabled={actionLoading}
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="decline-quote-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Decline
                </Button>
              </>
            )}
            {amendMode && (
              <>
                <Button
                  onClick={handleRequestAmendment}
                  disabled={actionLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="request-amended-quote-btn"
                >
                  <PenLine className="h-4 w-4 mr-2" />
                  Request Amended Quote
                </Button>
                <Button
                  onClick={cancelAmendMode}
                  disabled={actionLoading}
                  variant="outline"
                  data-testid="cancel-amend-btn"
                >
                  Cancel
                </Button>
              </>
            )}
            {canAdjustQty && !adjustMode && !amendMode && !showQuoteActions && (
              <Button
                onClick={enterAdjustMode}
                disabled={actionLoading}
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="adjust-qty-btn"
              >
                <PenLine className="h-4 w-4 mr-2" />
                Adjust Quantity
              </Button>
            )}
            {adjustMode && (
              <>
                <Button
                  onClick={handleAdjustQuantity}
                  disabled={actionLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="submit-adjust-qty-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm Adjustment
                </Button>
                <Button
                  onClick={cancelAdjustMode}
                  disabled={actionLoading}
                  variant="outline"
                  data-testid="cancel-adjust-btn"
                >
                  Cancel
                </Button>
              </>
            )}
            {canDownloadWaybill && (
              <Button 
                onClick={() => downloadPDF('waybill')} 
                variant="outline"
                data-testid="download-waybill-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Waybill
              </Button>
            )}
            {waybill.quoted_amount && (
              <Button 
                onClick={() => downloadPDF('quote')} 
                variant="outline"
                data-testid="download-quote-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                Download Quote
              </Button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quote Summary (if available) */}
            {waybill.quoted_amount && (
              <Card className="card-elevated border-l-4 border-l-teal-600" data-testid="quote-summary">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Quoted Amount</p>
                      <p className="text-3xl font-bold text-teal-700">{formatCurrency(waybill.quoted_amount)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sender & Receiver */}
            <div className="grid sm:grid-cols-2 gap-6">
              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5 text-teal-600" />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wide text-slate-500">Sender</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold text-slate-900">{waybill.sender_company}</p>
                  <p className="text-sm text-slate-600 mt-1">{waybill.sender_address}</p>
                  <p className="text-sm text-slate-600 mt-2">{waybill.sender_contact_person}</p>
                  <p className="text-sm text-slate-500">{waybill.sender_contact_number}</p>
                  <p className="text-sm text-slate-500">{waybill.sender_email}</p>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <Send className="h-5 w-5 text-purple-600" />
                    <CardTitle className="text-sm font-semibold uppercase tracking-wide text-slate-500">Receiver</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold text-slate-900">{waybill.receiver_company}</p>
                  <p className="text-sm text-slate-600 mt-1">{waybill.receiver_address}</p>
                  <p className="text-sm text-slate-600 mt-2">{waybill.receiver_contact_person}</p>
                  <p className="text-sm text-slate-500">{waybill.receiver_contact_number}</p>
                  <p className="text-sm text-slate-500">{waybill.receiver_email}</p>
                </CardContent>
              </Card>
            </div>

            {/* Delivery Type */}
            {(waybill.delivery_type_name || amendMode) && (
              <Card className={`card-elevated ${amendMode ? 'ring-2 ring-amber-300' : ''}`}>
                <CardContent className="p-4 flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                    <Truck className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Delivery Type</p>
                    {amendMode ? (
                      <select
                        value={amendedDeliveryType}
                        onChange={(e) => setAmendedDeliveryType(e.target.value)}
                        className="mt-1 w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                        data-testid="amend-delivery-type-select"
                      >
                        <option value="">Select delivery type</option>
                        {deliveryTypes.map(dt => (
                          <option key={dt.code} value={dt.code}>{dt.name}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="font-semibold text-slate-900">{waybill.delivery_type_name}</p>
                    )}
                  </div>
                  {!amendMode && waybill.delivery_expected_date && (
                    <div className="ml-auto text-right shrink-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Expected Delivery</p>
                      <p className="font-semibold text-slate-900">{formatDate(waybill.delivery_expected_date)}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Items */}
            <Card className={`card-elevated ${amendMode ? 'ring-2 ring-amber-300' : adjustMode ? 'ring-2 ring-purple-300' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  <CardTitle className="card-title">Items ({waybill.items?.length || 0})</CardTitle>
                  {amendMode && (
                    <span className="ml-auto text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                      Editing
                    </span>
                  )}
                  {adjustMode && (
                    <span className="ml-auto text-xs font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                      Adjusting Qty
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Qty</th>
                        <th>Weight</th>
                        <th>Dimensions</th>
                        {!amendMode && <th className="text-right">Quoted Amount</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {waybill.items?.map((item, idx) => {
                        const lineItem = waybill.quote_breakdown?.line_items?.find(li => li.item_id === item.item_id);
                        const amended = amendedItems.find(a => a.item_id === item.item_id);
                        const adjusted = adjustedItems.find(a => a.item_id === item.item_id);
                        const adjustedLineTotal = adjustMode && lineItem?.unit_cost && adjusted
                          ? lineItem.unit_cost * adjusted.quantity : null;
                        return (
                          <tr key={idx}>
                            <td className="capitalize">{item.package_type}</td>
                            <td>{item.description || '-'}</td>
                            <td>
                              {amendMode ? (
                                <input
                                  type="number"
                                  min="1"
                                  value={amended?.quantity ?? item.quantity}
                                  onChange={(e) => updateAmendedQty(item.item_id, e.target.value)}
                                  className="w-20 border border-slate-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                                  data-testid={`amend-qty-${idx}`}
                                />
                              ) : adjustMode ? (
                                <input
                                  type="number"
                                  min="1"
                                  value={adjusted?.quantity ?? item.quantity}
                                  onChange={(e) => updateAdjustedQty(item.item_id, e.target.value)}
                                  className="w-20 border border-slate-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400"
                                  data-testid={`adjust-qty-${idx}`}
                                />
                              ) : (
                                item.quantity
                              )}
                            </td>
                            <td>{item.weight} kg</td>
                            <td>{item.length}×{item.width}×{item.height} cm</td>
                            {!amendMode && (
                              <td className="text-right font-medium text-slate-900">
                                {adjustMode && adjustedLineTotal != null ? (
                                  <span className={adjusted?.quantity !== item.quantity ? 'text-purple-700' : ''}>
                                    {formatCurrency(adjustedLineTotal)}
                                  </span>
                                ) : (
                                  lineItem?.line_total ? formatCurrency(lineItem.line_total) : '-'
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {adjustMode && (
                  <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-purple-50/50">
                    <span className="text-sm text-slate-600">New Total</span>
                    <span className="text-lg font-bold text-purple-700" data-testid="adjusted-total">
                      {formatCurrency(getAdjustedTotal())}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tracking Timeline */}
            <Card className="card-elevated" data-testid="tracking-timeline">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <Truck className="h-5 w-5 text-indigo-600" />
                  <CardTitle className="card-title">Tracking Timeline</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {waybill.tracking_events?.length > 0 ? (
                  <div className="space-y-4">
                    {waybill.tracking_events.map((event, idx) => (
                      <div key={event.event_id || `te-${idx}`} className="flex">
                        <div className="flex flex-col items-center mr-4">
                          <div className={`w-3 h-3 rounded-full ${idx === 0 ? 'bg-teal-600' : 'bg-slate-300'}`}></div>
                          {idx < waybill.tracking_events.length - 1 && (
                            <div className="w-0.5 h-full bg-slate-200 my-1"></div>
                          )}
                        </div>
                        <div className="pb-4">
                          <div className="flex items-center space-x-2">
                            <StatusBadge status={event.status} />
                            <span className="text-xs text-slate-500">{formatDateTime(event.timestamp)}</span>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{event.location}</p>
                          {event.notes && <p className="text-sm text-slate-500 mt-1">{event.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No tracking events yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Summary Card */}
            <Card className="card-elevated">
              <CardHeader className="pb-3">
                <CardTitle className="card-title">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Reference</span>
                  <span className="text-sm font-medium text-slate-900">{waybill.reference_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Submitted</span>
                  <span className="text-sm font-medium text-slate-900">{formatDate(waybill.submission_date)}</span>
                </div>
                {waybill.quoted_date && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Quoted</span>
                    <span className="text-sm font-medium text-slate-900">{formatDate(waybill.quoted_date)}</span>
                  </div>
                )}
                {waybill.accepted_date && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Accepted</span>
                    <span className="text-sm font-medium text-slate-900">{formatDate(waybill.accepted_date)}</span>
                  </div>
                )}
                {waybill.estimated_delivery_date && (
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Est. Delivery</span>
                    <span className="text-sm font-medium text-teal-700">{formatDate(waybill.estimated_delivery_date)}</span>
                  </div>
                )}
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500">Total Items</span>
                    <span className="text-sm font-medium text-slate-900">{waybill.items?.length || 0}</span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-sm text-slate-500">Total Weight</span>
                    <span className="text-sm font-medium text-slate-900">
                      {waybill.items?.reduce((sum, item) => sum + (item.weight * item.quantity), 0).toFixed(2)} kg
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            {waybill.customer_notes && (
              <Card className="card-elevated">
                <CardHeader className="pb-3">
                  <CardTitle className="card-title">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">{waybill.customer_notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default WaybillDetail;
